import React, {
  useState, useEffect, useRef, useCallback
} from 'react';
import Const from '../../../config/const';
import { purchaseDetailsTypes } from '../../../../common/config/const';
import classes from './classes.module.scss';
import ContinueCallFooter from './continue_call';
import StartCallFooter from './start_call';
import withPGTranslation from '../../../config/withPGTranslation';
import { log, LOG_LEVELS } from '../../../../common/config/app_logger';
import DurationSelector from './duration_selector/duration_selector';
import {
  setDefaultTransform, setDefaultSelected, renderTitle, renderThisChargeTakesIntoAccountLabel, renderInfoLabel, renderAdditionalOption, renderSaveUpLabel, renderZenModeLabel, renderNewUserPaygAvailableBalanceLabel,
  renderDurationItemDiscountLabelForNewUser
} from './select_chat_credit_utils';
import { formatLocalCurrency } from '../../../config/util';
import Spiner from '../../../../common/helpers/spiner';
import { isWebView, convertStringToCamelCase } from '../../../../common/config/utils';
import { SvgIcon } from '../../../../common/sprites';

const { shared } = window;

const durationChanged = (selected, option, setDefaultDurationChanged) => {
  if (selected.duration && !(option.duration === selected.duration) && !option.default) setDefaultDurationChanged(true);
  if (option.default)  setDefaultDurationChanged(false);
};

const renderClientAvailableCredit = ({ loading, clientAvailableCredit, currency }) => {
  if (loading) return <span className={ classes.clientAvailableCreditSkeleton } />;
  return `${ formatLocalCurrency(clientAvailableCredit, currency) }`;
};

function SelectChatCredit({
  pricingOptions = [],
  cancelChat,
  selectCredit,
  advisorId,
  name,
  selectDurationOption,
  isContinue = false,
  localAvailableCredit,
  currency,
  t,
  loadPricingOptions,
  loading = false,
  selectedCarouselIndex,
  disabledStartChatButton,
  chatType,
  displayAffiliatePromoPopupAction,
  userShowMatchUpBonusInfo = null,
  clearAffiliatePromoPopup,
  isNewUser = false,
  selectorOpenedTime,
  selectDurationKind,
  advisorXfmProgram = {},
  userXfmProgram = {},
  newUserPaygPricingOption,
  userAdvisorModeSalePrice = null,
  texts = {},
  localPromoPrices = {},
  advisorModePricePerMinute,
  matchUpBonusInfo = {},
  showAffiliatePromoPopup = false,
  trackDurationPickExit
}) {
  const [selected, setSelected] = useState({});
  const [defaultDurationChanged, setDefaultDurationChanged] = useState(false);
  const [selectedCarouselDefaultItem, setSelectedCarouselDefaultItem] = useState(0);
  const carouselRef = useRef(null);
  const additionalOption = pricingOptions.find(({ additional }) => additional);

  const onBackButtonEvent = () => {
    if (!isContinue) {
      cancelChat();
    }
  };

  const onSelect = (option) => {
    log(LOG_LEVELS.INFO, 'Chat', `selected option ${ JSON.stringify(option.selectCredit) } isContinue: ${ isContinue }`);
    durationChanged(selected, option, setDefaultDurationChanged);
    setSelected(option);
  };

  useEffect(() => {
    setDefaultTransform(selected.selectedCarouselIndex, carouselRef, pricingOptions);
  }, [selected]);

  useEffect(() => {
    if (((!pricingOptions || pricingOptions.length === 0) && !isContinue)) {
      loadPricingOptions(advisorId);
    }
    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener('popstate', onBackButtonEvent);
    return () => {
      window.removeEventListener('popstate', onBackButtonEvent);
    };
  }, []);

  useEffect(() => {
    setDefaultSelected(onSelect, pricingOptions, selectedCarouselIndex, setSelectedCarouselDefaultItem, setDefaultDurationChanged, newUserPaygPricingOption, isNewUser);
  }, [pricingOptions, newUserPaygPricingOption]);

  const startChat = () => {
    log(LOG_LEVELS.INFO, 'Chat', `on start chat isContinue: ${ isContinue }`);
    const exitTime = new Date();
    const time = exitTime - selectorOpenedTime;
    selectCredit(selected, time, defaultDurationChanged,  isContinue);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { startChat(); }
  };

  const onClickCurrentBalance = () => {
    log(LOG_LEVELS.INFO, 'Chat', `on use current balance, isContinue: ${ isContinue }`);
    const exitTime = new Date();
    const time = exitTime - selectorOpenedTime;
    onSelect(additionalOption);
    selectCredit(additionalOption, time, defaultDurationChanged, isContinue);
  };

  const renderClientCreditBalance = () => (
    localAvailableCredit > 0 && selectDurationKind !== Const.selectDurationKind.newUserPayg ? (
      <div
        className={ classes.clientCreditBalanceContainer }
      >
        { t('chat.credit_balance') }
        :
        {' '}
        {renderClientAvailableCredit({ loading, clientAvailableCredit: parseFloat(localAvailableCredit).toFixed(2), currency })}
      </div>
    ) : null
  );

  const renderFooter = () => {
    const { duration, descriptionKey } = selected;
    const infoLabelText = descriptionKey ? texts[convertStringToCamelCase(descriptionKey)] : null;
    const content = isContinue ? (
      <ContinueCallFooter chatType={ chatType } option={ selected } selectedDuration={ duration } defaultDurationChanged={ defaultDurationChanged } selectorOpenedTime={ selectorOpenedTime } />
    ) : (
      <StartCallFooter option={ selected } chatType={ chatType } defaultDurationChanged={ defaultDurationChanged } selectorOpenedTime={ selectorOpenedTime } selectDurationKind={ selectDurationKind } />
    );

    return (
      <div className={ classes.infoBottomContainer }>
        { renderThisChargeTakesIntoAccountLabel({ duration, localAvailableCredit, t }) }
        { renderInfoLabel({ infoLabelText, selectDurationKind }) }
        {content}
        { renderNewUserPaygAvailableBalanceLabel({ selectDurationKind, localAvailableCredit, t }) }
        { renderZenModeLabel({ selectDurationKind, infoLabelText }) }
      </div>
    );
  };

  const onCloseIconClick = () => {
    const exitTime = new Date();
    const time = exitTime - selectorOpenedTime;
    const arg = chatType === Const.chatType.text ? selected : selected.duration;
    clearAffiliatePromoPopup();
    trackDurationPickExit(arg, time, defaultDurationChanged);
    cancelChat();
  };

  const onCloseIconClickRef = useRef(null);
  onCloseIconClickRef.current = onCloseIconClick;
  const stableOnCloseIconClick = useCallback(() => onCloseIconClickRef.current(), []);

  const renderCloseIcon = () => (isContinue ? null : (
    <button type="button" className={ classes.closeBtn } onClick={ onCloseIconClick }>
      <SvgIcon id="close_icon" className={ classes.closeIcon } size={ 20 } />
    </button>
  ));

  useEffect(() => {
    if (showAffiliatePromoPopup) return;
    if (matchUpBonusInfo
        && Object.keys(matchUpBonusInfo).length !== 0
        && (userShowMatchUpBonusInfo === null || userShowMatchUpBonusInfo)
        && Object.keys(selected).length !== 0) {
      const additionParams = {
        noNeedCloseIcon: true, chatType,  needUpdatePricingOptions: true, onSelectDuration: true, onCloseIconClick: stableOnCloseIconClick
      };
      if (!isWebView()) {
        displayAffiliatePromoPopupAction(matchUpBonusInfo, purchaseDetailsTypes.AFFILIATE_PROMO_POPUP, additionParams);
      }
    }
  }, [matchUpBonusInfo, userShowMatchUpBonusInfo, selected, showAffiliatePromoPopup]);

  const onSelectDurationOption = (option, index) => {
    selectDurationOption(option, isContinue, isNewUser, index);
    onSelect({ ...option, selectedCarouselIndex: index });
  };

  const onDurationClick = (option, index) => (!isNewUser ? startChat()
    : onSelectDurationOption(option, index));

  const getDurationsSelectorProps = () => {
    const durationsSelectorProps = {
      selected, isContinue, pricingOptions, onDurationClick, selectedCarouselDefaultItem, disabledStartChatButton, carouselRef,
      isNewUser, setDefaultTransform, onKeyDown, loading, onSelect, selectedCarouselIndex, selectDurationKind, advisorXfmProgram,
      userXfmProgram, localAvailableCredit, userAdvisorModeSalePrice, texts, localPromoPrices, advisorModePricePerMinute
    };
    return durationsSelectorProps;
  };

  return (
    <div key={ `SelectChatCredit__${ localAvailableCredit }_${ userAdvisorModeSalePrice }_${ loading }` } className={ classes.container }>
      { loading ? <Spiner loaderColor={ shared.appColor } className={ classes.loader } /> : null }
      {renderCloseIcon()}
      {renderSaveUpLabel({ newUserPaygPricingOption })}
      <div className={ selectDurationKind === Const.selectDurationKind.newUser ? classes.newUserContainerWithIcon : classes.containerWithIcon }>
        <div className={ classes.dialogContainer } style={ selectDurationKind === Const.selectDurationKind.newUserPayg ? { background: 'var(--app-white)' } : {}  }>
          <div className={ classes.selectTitleContainer } style={ { marginTop: isContinue && selectDurationKind === Const.selectDurationKind.newUser ? '80px' : '40px' } }>
            <b>{renderTitle(chatType, isContinue, name, t, selectDurationKind)}</b>
          </div>
          <DurationSelector key={ `DurationSelector_${ localAvailableCredit }_${ userAdvisorModeSalePrice }_${ loading }` } { ...getDurationsSelectorProps() } />
          { renderDurationItemDiscountLabelForNewUser({ discountData: selected.discountData, selectDurationKind, t }) }
          {renderAdditionalOption(additionalOption, onClickCurrentBalance, onKeyDown, t, selectDurationKind) || renderClientCreditBalance()}
          {renderFooter()}
        </div>
      </div>
    </div>
  );
}

export default withPGTranslation(SelectChatCredit);
